import React, { useEffect, useState } from "react";
import { getSiteDescription, replaceLegacySiteName, useContent } from "../lib/content";
import { isHtmlString, normalizeRichTextHtml } from "../lib/sanitize";
import { controlledTypeStyle } from "../lib/typography";

function fontSize(value, fallback, min, max) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
  return `clamp(${min}px, ${safe / 14}vw, ${safe}px)`;
}

export default function Hero() {
  const [time, setTime] = useState("");
  const { content } = useContent();

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const opts = {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Berlin",
      };
      setTime(new Intl.DateTimeFormat("en-GB", opts).format(now) + " CET");
    };
    update();
    const i = setInterval(update, 30000);
    return () => clearInterval(i);
  }, []);

  const eyebrow = content?.hero_eyebrow ?? "";
  const line1 = content?.hero_line1 ?? "";
  const line2 = content?.hero_line2 ?? "";
  const line3Pre = content?.hero_line3_pre ?? "";
  const line3Accent = content?.hero_line3_accent ?? "";
  const subtext = replaceLegacySiteName(
    content?.hero_subtext ||
      content?.site_description ||
      getSiteDescription(content),
    content
  );
  const cta = content?.hero_cta ?? "View work";
  const line1Size = fontSize(content?.typography_hero_line1_size, 132, 20, 180);
  const line2Size = fontSize(content?.typography_hero_line2_size, 132, 20, 180);
  const line3Size = fontSize(content?.typography_hero_line3_size, 132, 20, 180);

  return (
    <section
      data-testid="hero-section"
      className="scroll-effect-section site-section-pad-lg relative pt-40 pb-24 md:pt-56 md:pb-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-10 -right-40 h-[520px] w-[520px] rounded-full bg-ember/20 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-20 h-[320px] w-[320px] rounded-full bg-bone-200/5 blur-[120px]"
      />

      <div className="container relative">
        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-bone-200/50 mb-10 animate-fade-up">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-ember animate-pulse"
            data-testid="hero-live-dot"
          />
          <span data-testid="hero-eyebrow">{eyebrow}</span>
          <span className="hidden md:inline">/ {time}</span>
        </div>

        <h1
          data-testid="hero-heading"
          className="font-display text-[14vw] md:text-[9.5vw] leading-[0.85] tracking-tightest text-balance"
          style={{ letterSpacing: 0 }}
        >
          <span
            className="block animate-fade-up"
            style={controlledTypeStyle({
              fontSize: line1Size,
              font: content?.typography_hero_line1_font,
            })}
          >
            {line1}
          </span>
          <span
            className="block italic text-bone-200/70 animate-fade-up"
            style={{
              animationDelay: "160ms",
              ...controlledTypeStyle({
                fontSize: line2Size,
                font: content?.typography_hero_line2_font,
              }),
            }}
          >
            {line2}
          </span>
          <span
            className="block animate-fade-up"
            style={{
              animationDelay: "240ms",
              ...controlledTypeStyle({
                fontSize: line3Size,
                font: content?.typography_hero_line3_font,
              }),
            }}
          >
            {line3Pre}
            <span className="text-ember">{line3Accent}</span>
          </span>
        </h1>

        <div className="mt-16 grid md:grid-cols-12 gap-10 items-end">
          <div
            data-testid="hero-subtext"
            className="md:col-span-12 text-base md:text-lg text-bone-200/70 leading-relaxed animate-fade-up"
            style={{ animationDelay: "380ms" }}
          >
            {isHtmlString(subtext) ? (
              <div
                className="prose-rich"
                dangerouslySetInnerHTML={{ __html: normalizeRichTextHtml(subtext) }}
              />
            ) : (
              <p>{subtext}</p>
            )}
          </div>
          <div
            className="md:col-span-12 flex md:justify-end animate-fade-up"
            style={{ animationDelay: "460ms" }}
          >
            <a
              href="#work"
              data-testid="hero-view-work"
              className="site-button group inline-flex items-center gap-4 font-mono text-xs uppercase tracking-[0.3em] text-bone-50 border border-transparent px-4 py-3"
            >
              <span className="h-px w-12 bg-bone-50 group-hover:w-20 transition-all duration-500" />
              <span>{cta}</span>
              <span className="opacity-60 group-hover:translate-x-1 transition-transform">↓</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
